import { useQuery } from '@tanstack/react-query';
import type { Screenshot } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { ActivityBar, Card, EmptyState, ErrorNote, PageHeader, Spinner } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Transparency view: shows every user exactly what has been captured about
 * them, which is what the monitoring notice promises.
 */
export function MyActivityPage() {
  const { settings } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['screenshots', 'mine'],
    queryFn: () => apiFetch<Screenshot[]>('/api/screenshots/mine/recent'),
  });

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="My activity" description="Everything the desktop app has captured about you, most recent first." />

      <div className="mb-4 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900">
        <p className="font-medium">How monitoring works here</p>
        <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-brand-800">
          <li>Screenshots are taken only while your timer is running in the desktop app — never in the web app, and never when you are clocked out.</li>
          <li>Captures happen every {settings?.screenshotIntervalMinutes ?? 10} minutes, and the app notifies you each time.</li>
          <li>Activity level counts how many keystrokes and mouse events happened — the keys themselves are never recorded.</li>
          <li>Your administrators and the managers of your projects can view these images.</li>
        </ul>
      </div>

      <ErrorNote error={error} />

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : (data?.length ?? 0) === 0 ? (
          <EmptyState title="Nothing captured yet" description="Screenshots appear here after you clock in from the desktop app." />
        ) : (
          <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4">
            {data?.map((shot) => (
              <figure key={shot.id}>
                <img
                  src={shot.thumbnailUrl}
                  alt={`Your screen at ${formatDateTime(shot.capturedAt)}`}
                  loading="lazy"
                  className="aspect-video w-full rounded-lg border border-slate-200 object-cover"
                />
                <figcaption className="mt-1.5 space-y-1">
                  <p className="text-xs text-slate-600">{formatDateTime(shot.capturedAt)}</p>
                  <p className="truncate text-xs text-slate-400">{shot.projectName ?? 'No project'}</p>
                  <ActivityBar level={shot.activityLevel} />
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
